/**
 * Delayed purchase — the boolean flag that replaced the "Delayed Purchase" loan
 * purpose (request board REQ-106, backend 2026-10-19).
 *
 * Backend contract (eastview-os `frontend_documentation/delayed-purchase-flag.md`):
 *   - `delayedPurchase: bool | null` on the deal (loan model) and on the DSCR
 *     quick-price input. The RTL / GUC sizers post loan-model vocabulary, so it
 *     rides on their scenario too.
 *   - Meaningful ONLY when `loanPurpose` is "Refinance - Cash Out" — the backend
 *     clears it otherwise. Definition: the property was bought within the last
 *     6 months, with no outstanding debt.
 *   - "Delayed Purchase" is no longer a `loanPurpose` option, but legacy stored
 *     values are still read, and read the same as cash-out + flag.
 *
 * Parity rule: everywhere the old string was read, the flag behaves exactly as
 * the string did — through ONE helper, `is_delayed_purchase(purpose, flag)` in
 * the backend's pipeline model. `isDelayedPurchase` below is its FE twin; use it
 * (or `isPurchaseLike`) instead of testing the purpose string, or the two halves
 * of the app will disagree about the same loan.
 */
export const DELAYED_PURCHASE_FIELD = "delayedPurchase";
export const CASH_OUT_REFI = "Refinance - Cash Out";
/** The retired purpose value — read for legacy loans/scenarios, never written. */
export const LEGACY_DELAYED_PURCHASE = "Delayed Purchase";

export const DELAYED_PURCHASE_LABEL = "Delayed purchase?";
export const DELAYED_PURCHASE_HINT =
  "Bought within the last 6 months with no outstanding debt. Priced and documented as a purchase.";

/** Whether the "Delayed purchase?" question applies (shows) for this purpose. */
export const canBeDelayedPurchase = (purpose) => purpose === CASH_OUT_REFI;

/** The backend's `is_delayed_purchase(purpose, flag)`. */
export const isDelayedPurchase = (purpose, flag) =>
  purpose === LEGACY_DELAYED_PURCHASE ||
  (purpose === CASH_OUT_REFI && flag === true);

/**
 * Purchase for every purchase-only rule — purchase price required, purchase
 * documents, the purchase leverage branch / cost basis, purchase-only fields.
 * A delayed purchase counts (that is the parity rule); a plain refinance not.
 */
export const isPurchaseLike = (purpose, flag) =>
  purpose === "Purchase" || isDelayedPurchase(purpose, flag);

/** Same, read straight off a loan / scenario / form-values object. */
export const isPurchaseLikeLoan = (obj) =>
  isPurchaseLike(obj?.loanPurpose, obj?.[DELAYED_PURCHASE_FIELD]);

/**
 * A legacy record ("Delayed Purchase" purpose) in the NEW shape: cash-out refi
 * + the flag — what the backend's writers now emit. Anything else is returned
 * unchanged. Use when seeding a form from stored data, so an edit-and-save
 * writes the new shape rather than the retired value.
 */
export const normalizeLegacyDelayedPurchase = (obj) =>
  obj?.loanPurpose === LEGACY_DELAYED_PURCHASE
    ? { ...obj, loanPurpose: CASH_OUT_REFI, [DELAYED_PURCHASE_FIELD]: true }
    : obj;
